import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormBuilder,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Switch,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import { AIAssistDrawer, LastSuggestionPanel, PromptInputButton } from "@eduviaos/capability-ai-ui";
import type { FormField } from "@eduviaos/shared/types";
import { ApiError } from "@eduviaos/shared";
import type { CapabilityPromptContextWire } from "@eduviaos/shared";
import { TenantDetailBreadcrumbs } from "@/components/AppBreadcrumbs";
import { useTenantForm, useUpdateTenantForm } from "@/hooks/useTenantForm";
import { useSaCapabilityAiAssist } from "@/hooks/useCapabilityAiAssist";
import { api } from "@/lib/api";

/** Minimal slice of the SA tenants list we need for AI prompt context:
 *  the tenant's `type` (= vertical) and enabled modules. Reuses the same
 *  query key/endpoint as TenantDetailPage so react-query dedupes. */
interface TenantSummaryRow {
  slug: string;
  type: string;
  feature_flags: { enabled_modules?: string[] | null };
}
interface TenantsListResponse {
  items: TenantSummaryRow[];
  total: number;
}

interface MetaState {
  name: string;
  purpose: string;
  submit_button_label: string;
  success_message: string;
  requires_auth: boolean;
}

/**
 * SA cross-tenant form editor (Phase B2.2).
 *
 * Lives at `/tenants/:slug/forms/:formSlug/edit` and is the only
 * entry-point for editing a tenant's form fields from the SA portal.
 * The previous SA-level `/forms/new` editor was orphaned — every form
 * is tenant-scoped, so a SA-level editor never had a tenant context
 * to write to.
 *
 * Wires:
 *   - GET  /super-admin/tenants/{slug}/forms/{form_slug} → hydrate
 *   - PATCH /super-admin/tenants/{slug}/forms/{form_slug} → save
 */
export function TenantFormEditorPage() {
  const { slug = "", formSlug = "" } = useParams<{ slug: string; formSlug: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const formQuery = useTenantForm(slug, formSlug);
  const updateMutation = useUpdateTenantForm(slug, formSlug);

  const [meta, setMeta] = React.useState<MetaState>({
    name: "",
    purpose: "custom",
    submit_button_label: "Submit",
    success_message: "Thank you. Your response has been recorded.",
    requires_auth: true,
  });
  const [fields, setFields] = React.useState<FormField[]>([]);
  const [hydrated, setHydrated] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);

  // Version-aware (re-)hydration. We seed the buffers from the BE form on
  // first load AND whenever the server `version` changes — the latter is what
  // makes L4 AI-apply work: a field apply mints v+1 on the BE, we invalidate
  // the detail query, and the fresh version re-seeds the editor so the new
  // field shows up. AI assist is gated behind a clean buffer (see
  // `startFieldAssist`), so re-seeding never clobbers in-progress edits.
  const syncedVersionRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const data = formQuery.data;
    if (!data || syncedVersionRef.current === data.version) return;
    syncedVersionRef.current = data.version;
    setMeta({
      name: data.name,
      purpose: data.purpose,
      submit_button_label: data.submit_button_label,
      success_message: data.success_message,
      requires_auth: data.requires_auth,
    });
    setFields(data.fields.slice().sort((a, b) => a.order - b.order));
    setHydrated(true);
  }, [formQuery.data]);

  // ── L4 AI config-assist (Phase F-finale-4 Slice 6b-FE) ──────────────
  // Field-level assist scoped to THIS form. The prompt context's vertical +
  // enabled_modules come from the tenant summary (reused list cache); the
  // form is pinned via `available_form_slugs: [formSlug]` so the model emits
  // a FieldSuggestion targeting this form's slug, which the BE persists into.
  const tenantsQuery = useQuery({
    queryKey: ["super-admin", "tenants"],
    queryFn: () => api.get<TenantsListResponse>("/super-admin/tenants"),
  });
  const tenant = tenantsQuery.data?.items.find((t) => t.slug === slug) ?? null;

  const buildContext = React.useCallback(
    (): CapabilityPromptContextWire => ({
      tenant_id: slug,
      vertical: tenant?.type ?? "",
      enabled_modules: tenant?.feature_flags.enabled_modules ?? [],
      assessment_template_slugs: [],
      existing_stages: [],
      // Scope generation to the form being edited so the FieldSuggestion node
      // carries this form's slug (the BE field-apply route persists by slug).
      available_form_slugs: [formSlug],
      // Forms use the forms-domain versioned-PATCH contract (every edit = new
      // version) and have no admission-style CAS token. The field-apply route
      // ignores `draft_version`; 0 is a truthful "not applicable" sentinel.
      draft_version: 0,
    }),
    [slug, formSlug, tenant],
  );

  const onAiApplied = React.useCallback(() => {
    // Field apply minted a new active form version on the BE. Refresh the
    // detail (re-seeds the editor to the new version + field via the effect
    // above) and the tenant-forms list (field counts on the Forms tab).
    void qc.invalidateQueries({ queryKey: ["super-admin", "tenant-form", slug, formSlug] });
    void qc.invalidateQueries({ queryKey: ["super-admin", "tenant-forms", slug] });
    toast.success("AI field added — saved as a new form version.");
  }, [qc, slug, formSlug]);

  const fieldAssist = useSaCapabilityAiAssist({
    tenantSlug: slug,
    targetKind: "field",
    buildContext,
    onApplied: onAiApplied,
  });

  const onSave = async () => {
    try {
      await updateMutation.mutateAsync({
        name: meta.name,
        purpose: meta.purpose,
        submit_button_label: meta.submit_button_label,
        success_message: meta.success_message,
        requires_auth: meta.requires_auth,
        fields,
      });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1400);
    } catch {
      // Surfaced below from updateMutation.error
    }
  };

  if (formQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (formQuery.isError || !formQuery.data) {
    const message =
      formQuery.error instanceof ApiError
        ? formQuery.error.message
        : ((formQuery.error as Error)?.message ?? "Could not load form.");
    return (
      <div className="grid gap-3 py-12 text-center">
        <p className="text-destructive text-sm">{message}</p>
        <Link to={`/tenants/${slug}`} className="text-sm underline">
          Back to tenant
        </Link>
      </div>
    );
  }

  const data = formQuery.data;
  const dirty =
    hydrated &&
    (meta.name !== data.name ||
      meta.purpose !== data.purpose ||
      meta.submit_button_label !== data.submit_button_label ||
      meta.success_message !== data.success_message ||
      meta.requires_auth !== data.requires_auth ||
      JSON.stringify(fields) !==
        JSON.stringify(data.fields.slice().sort((a, b) => a.order - b.order)));

  const submitting = updateMutation.isPending;
  const canSave = meta.name.trim().length > 0 && fields.length > 0 && !submitting && dirty;

  const errorMessage =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : (updateMutation.error as Error | undefined)?.message;

  // Gate AI assist behind a clean buffer: an in-progress field/meta edit must
  // be saved or discarded first, otherwise the new version minted by apply
  // would silently overwrite it. Mirrors the AdmissionsPanel gate.
  const startFieldAssist = () => {
    if (dirty) {
      toast.error("Save or discard your unsaved changes before using AI assist.");
      return;
    }
    fieldAssist.start();
  };
  // The prompt context needs the tenant's vertical; disable the trigger until
  // the tenant summary resolves so we never request against an empty vertical.
  const aiDisabled = !tenant;

  return (
    <div className="grid gap-5">
      <PageHeader>
        <PageHeaderTop>
          <TenantDetailBreadcrumbs
            tenantName={slug}
            trail={[{ label: "Forms", to: `/tenants/${slug}` }, { label: meta.name || data.name }]}
          />
          <PageHeaderActions>
            <Badge variant={data.requires_auth ? "default" : "success"}>
              {data.requires_auth ? "internal" : "public"}
            </Badge>
            <Badge variant="warn">v{data.version} active</Badge>
            <PromptInputButton
              onClick={startFieldAssist}
              label="AI assist"
              data-testid="ai-assist-field"
              disabled={aiDisabled}
            />
            <Button onClick={onSave} disabled={!canSave} data-testid="form-save">
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : savedFlash ? (
                <>
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  Saved as v{updateMutation.data?.version ?? data.version}
                </>
              ) : (
                <>
                  <Save className="size-4" aria-hidden="true" />
                  Save new version
                </>
              )}
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>{meta.name || data.name}</PageHeaderTitle>
          <PageHeaderDescription>
            Tenant <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{slug}</code> · form{" "}
            <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{formSlug}</code> · saving
            spawns a new active version; the previous one is preserved in history.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      {errorMessage && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      <LastSuggestionPanel
        suggestion={fieldAssist.open ? null : fieldAssist.lastSuggestion}
        onReopen={fieldAssist.reopen}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metadata</CardTitle>
          <CardDescription>
            Display name, submit button, and success message shown to end-users when this form is
            submitted.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="meta-name">Name</Label>
            <Input
              id="meta-name"
              data-testid="meta-name"
              value={meta.name}
              onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="meta-purpose">Purpose</Label>
            <Input
              id="meta-purpose"
              value={meta.purpose}
              onChange={(e) => setMeta((m) => ({ ...m, purpose: e.target.value }))}
              placeholder="custom, admission_enquiry, exam_attempt..."
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="meta-submit">Submit button label</Label>
            <Input
              id="meta-submit"
              value={meta.submit_button_label}
              onChange={(e) => setMeta((m) => ({ ...m, submit_button_label: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5 md:col-span-2">
            <Label htmlFor="meta-success">Success message</Label>
            <Textarea
              id="meta-success"
              rows={2}
              value={meta.success_message}
              onChange={(e) => setMeta((m) => ({ ...m, success_message: e.target.value }))}
            />
          </div>
          <label className="border-border bg-muted/30 flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 md:col-span-2">
            <div className="grid gap-0.5">
              <span className="text-sm font-medium">Require authentication</span>
              <span className="text-muted-foreground text-xs">
                Public forms (off) accept anonymous submissions; tenant is resolved via the
                X-Tenant-Slug header.
              </span>
            </div>
            <Switch
              checked={meta.requires_auth}
              onCheckedChange={(v) => setMeta((m) => ({ ...m, requires_auth: !!v }))}
            />
          </label>
        </CardContent>
      </Card>

      <FormBuilder fields={fields} onChange={setFields} />

      {/* Sticky save dock — mirrors the Branding panel pattern so dirty
          state is always one click away regardless of how far the user
          has scrolled. */}
      {dirty && (
        <div className="sticky bottom-4 left-0 right-0 z-10">
          <div className="bg-card mx-auto flex max-w-2xl items-center justify-between rounded-md border px-4 py-2.5 shadow-lg">
            <p className="text-sm">
              <Badge variant="warn" className="mr-2 text-[10px]">
                Unsaved
              </Badge>
              Saving spawns <strong>v{data.version + 1}</strong> as the new active version.
            </p>
            <Button onClick={onSave} disabled={!canSave} size="sm">
              {submitting ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-3.5" aria-hidden="true" />
              )}
              Save
            </Button>
          </div>
        </div>
      )}

      {!dirty && (
        <p className="text-muted-foreground text-center text-xs">
          Cancel by{" "}
          <button type="button" className="underline" onClick={() => navigate(`/tenants/${slug}`)}>
            navigating back
          </button>
          .
        </p>
      )}

      <AIAssistDrawer
        open={fieldAssist.open}
        onOpenChange={fieldAssist.setOpen}
        targetKind="field"
        state={fieldAssist.state}
        onGenerate={fieldAssist.generate}
        onApply={fieldAssist.apply}
        onReject={fieldAssist.reject}
        onRetry={fieldAssist.retry}
      />
    </div>
  );
}
